import {
  BasicText,
  BoldText,
  StyledText
} from "views/PatientBoard/microViews/History/Attributes/Attributes.styles";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  FormControl,
  FormControlLabel,
  Radio,
  RadioGroup,
  Stack
} from "@mui/material";
import Button from "components/shared/Button/Button";
import React, { useState, useEffect, useCallback } from "react";
import API from "api";
import Session from "helpers/session";

interface InvalidateModalProps {
  open: boolean;
  onClose: () => void;
  onConfirm: (reason: string) => Promise<void> | void;
  readingId?: string;
  patientName?: string;
  description?: React.ReactNode;
  requireReason?: boolean;
  title?: string;
}

export default function InvalidateModal({
  open,
  onClose,
  onConfirm,
  readingId,
  patientName,
  description,
  requireReason = true,
  title: titleProp,
}: InvalidateModalProps) {
  const [reasons, setReasons] = useState<{ value: string; label: string; definition: string }[]>([]);
  const [reasonsState, setReasonsState] = useState<"idle" | "loading" | "loaded" | "error">("idle");
  const [selectedReason, setSelectedReason] = useState("");
  const [submitting, setSubmitting] = useState(false);

  const loadReasons = useCallback(() => {
    setReasonsState((prev) => {
      if (prev === "loading" || prev === "loaded") return prev; // already handled
      API.kore.readings
        .getInvalidateReasons(Session.crackCookieToken("provider"))
        .then((res: any) => { setReasons(Object.values(res)); setReasonsState("loaded"); })
        .catch(() => setReasonsState("error"));
      return "loading";
    });
  }, []);

  useEffect(() => {
    if (!open) return;
    setSelectedReason("");
    setSubmitting(false);
    loadReasons();
  }, [open, loadReasons]);

  const handleConfirm = async () => {
    if (requireReason && !selectedReason) return;
    setSubmitting(true);
    try {
      await onConfirm(selectedReason);
      onClose();
    } catch {
      setSubmitting(false);
    }
  };

  const title =
    titleProp ??
    (patientName && readingId
      ? `Invalidate reading ${readingId} for ${patientName}?`
      : "Invalidate Reading");

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle sx={{ fontWeight: "bold" }}>{title}</DialogTitle>
      <DialogContent>
        {description && <BasicText sx={{ mb: 2 }}>{description}</BasicText>}
        {requireReason && (
          reasonsState === "error" ? (
            <Stack spacing={1}>
              <BasicText>Couldn't load reasons.</BasicText>
              <Button
                variant="neutral"
                onClick={() => {
                  setReasonsState("loading");
                  API.kore.readings
                    .getInvalidateReasons(Session.crackCookieToken("provider"))
                    .then((res: Record<string, { value: string; label: string; definition: string }>) => {
                      setReasons(Object.values(res).filter((reason) => reason.value !== "revalidation"));
                      setReasonsState("loaded");
                    })
                    .catch(() => setReasonsState("error"));
                }}
              >
                Retry
              </Button>
            </Stack>
          ) : reasonsState !== "loaded" ? (
            <BasicText>Loading reasons…</BasicText>
          ) : (
            <FormControl>
              <RadioGroup
                value={selectedReason}
                onChange={(event) => setSelectedReason(event.target.value)}
              >
                {reasons.map((reason) => (
                  <FormControlLabel
                    key={reason.value}
                    value={reason.value}
                    control={<Radio />}
                    label={
                      <Stack>
                        <BoldText>{reason.label}</BoldText>
                        <StyledText>{reason.definition}</StyledText>
                      </Stack>
                    }
                  />
                ))}
              </RadioGroup>
            </FormControl>
          )
        )}
      </DialogContent>
      <DialogActions>
        <Button variant="neutral" onClick={onClose}>Cancel</Button>
        <Button
          variant="primary"
          onClick={handleConfirm}
          disabled={(requireReason && !selectedReason) || submitting}
        >
          Confirm
        </Button>
      </DialogActions>
    </Dialog>
  );
}